import { request } from '@umijs/max';

export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  permissions: string[];
  status: 'active' | 'inactive' | 'suspended';
  created_at: string;
  last_login?: string;
}

export interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
  resource: string;
  action: string;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  level: number;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  token: string;
  user: User;
  expires_in: number;
}

// 预定义权限
export const PERMISSIONS = {
  // Dashboard permissions
  DASHBOARD_VIEW: 'dashboard:view',
  DASHBOARD_EXECUTE: 'dashboard:execute',

  // Product management permissions
  PRODUCT_VIEW: 'product:view',
  PRODUCT_SYNC: 'product:sync',
  PRODUCT_PRICE_SYNC: 'product:price_sync',
  PRODUCT_STOCK_SYNC: 'product:stock_sync',
  PRODUCT_IMAGE_SYNC: 'product:image_sync',
  PRODUCT_GROUPING_SYNC: 'product:grouping_sync',
  PRODUCT_STATUS_CHECK: 'product:status_check',
  PRODUCT_UNPUBLISH: 'product:unpublish',
  PRODUCT_DELETE: 'product:delete',
  PRODUCT_DUPLICATE_CHECK: 'product:duplicate_check',
  PRODUCT_CLEAN_UNBOUND: 'product:clean_unbound',
  PRODUCT_BACKUP: 'product:backup',

  // DEAR permissions
  DEAR_MANUAL_SYNC: 'dear:manual_sync',
  DEAR_UNPUBLISH_CHECK: 'dear:unpublish_check',
  DEAR_SYNC_BY_BARCODE: 'dear:sync_by_barcode',
  DEAR_BACKFEED: 'dear:backfeed',

  // Order permissions
  ORDER_VIEW: 'order:view',
  ORDER_SYNC: 'order:sync',
  ORDER_STATUS_CHECK: 'order:status_check',

  // Log permissions
  LOG_VIEW: 'log:view',
  LOG_EXPORT: 'log:export',

  // Admin permissions
  USER_MANAGE: 'user:manage',
  ROLE_MANAGE: 'role:manage',
  PERMISSION_MANAGE: 'permission:manage',
  SYSTEM_CONFIG: 'system:config',
  ADMIN_SETTING: 'admin:setting',
} as const;

// 任务路径到权限的映射
export const TASK_PERMISSIONS: Record<string, string> = {
  '/api/product/status/check': PERMISSIONS.PRODUCT_STATUS_CHECK,
  '/api/product/sync': PERMISSIONS.PRODUCT_SYNC,
  '/api/product/sync/progress': PERMISSIONS.PRODUCT_SYNC,
  '/api/product/sync/cancel': PERMISSIONS.PRODUCT_SYNC,
  '/api/product/price/sync': PERMISSIONS.PRODUCT_PRICE_SYNC,
  '/api/product/stock': PERMISSIONS.PRODUCT_STOCK_SYNC,
  '/api/product/image': PERMISSIONS.PRODUCT_IMAGE_SYNC,
  '/api/product/grouping/sync': PERMISSIONS.PRODUCT_GROUPING_SYNC,
  '/api/product/duplicate_check': PERMISSIONS.PRODUCT_DUPLICATE_CHECK,
  '/api/product/unpublish': PERMISSIONS.PRODUCT_UNPUBLISH,
  '/api/product/clean-unbound': PERMISSIONS.PRODUCT_CLEAN_UNBOUND,
  '/api/product/delete': PERMISSIONS.PRODUCT_DELETE,
  '/api/product/backup': PERMISSIONS.PRODUCT_BACKUP,
  '/api/product/dear_tracker/manual_sync': PERMISSIONS.DEAR_MANUAL_SYNC,
  '/api/product/dear_tracker/manual_unpublish_check': PERMISSIONS.DEAR_UNPUBLISH_CHECK,
  '/api/product/dear_tracker/sync_by_barcode': PERMISSIONS.DEAR_SYNC_BY_BARCODE,
  '/api/product/dear_backfeed': PERMISSIONS.DEAR_BACKFEED,
  '/api/order/migration/import': PERMISSIONS.ORDER_SYNC,
  '/api/order/migration/jobs': PERMISSIONS.ORDER_SYNC,
  '/api/order/sync': PERMISSIONS.ORDER_SYNC,
  '/api/order/status/check': PERMISSIONS.ORDER_STATUS_CHECK,
  // 为了向后兼容，保留没有 /api 前缀的映射
  '/product/status/check': PERMISSIONS.PRODUCT_STATUS_CHECK,
  '/product/sync': PERMISSIONS.PRODUCT_SYNC,
  '/product/price/sync': PERMISSIONS.PRODUCT_PRICE_SYNC,
  '/product/stock': PERMISSIONS.PRODUCT_STOCK_SYNC,
  '/product/image': PERMISSIONS.PRODUCT_IMAGE_SYNC,
  '/product/grouping/sync': PERMISSIONS.PRODUCT_GROUPING_SYNC,
  '/product/duplicate_check': PERMISSIONS.PRODUCT_DUPLICATE_CHECK,
  '/product/unpublish': PERMISSIONS.PRODUCT_UNPUBLISH,
  '/product/clean-unbound': PERMISSIONS.PRODUCT_CLEAN_UNBOUND,
  '/product/delete': PERMISSIONS.PRODUCT_DELETE,
  '/product/backup': PERMISSIONS.PRODUCT_BACKUP,
  '/product/dear_tracker/manual_sync': PERMISSIONS.DEAR_MANUAL_SYNC,
  '/product/dear_tracker/manual_unpublish_check': PERMISSIONS.DEAR_UNPUBLISH_CHECK,
  '/product/dear_tracker/sync_by_barcode': PERMISSIONS.DEAR_SYNC_BY_BARCODE,
  '/product/dear_backfeed': PERMISSIONS.DEAR_BACKFEED,
  '/order/sync': PERMISSIONS.ORDER_SYNC,
  '/order/status/check': PERMISSIONS.ORDER_STATUS_CHECK,
  '/api/admin/setting': PERMISSIONS.ADMIN_SETTING,
  '/admin/setting': PERMISSIONS.ADMIN_SETTING,
};

// 角色定义
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  OPERATOR: 'operator',
  VIEWER: 'viewer',
} as const;

/** 规范化角色名（去空格、统一小写） */
export function normalizeRole(role?: string | null): string {
  return (role || '').trim().toLowerCase();
}

/** 是否为超级管理员 */
export function isSuperAdmin(user: Pick<User, 'role'> | null | undefined): boolean {
  return normalizeRole(user?.role) === ROLES.SUPER_ADMIN;
}

/**
 * 用户登录 - 从后端 API 验证
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  try {
    const response = await request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      data: credentials,
    });

    // 如果登录成功，保存用户信息和 token
    if (response.success && response.user && response.token) {
      const user = {
        ...response.user,
        role: normalizeRole(response.user.role),
      };
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('auth_token', response.token);
      return { ...response, user };
    }

    return response;
  } catch (error: any) {
    console.error('Login failed:', error);
    
    // 对于认证相关的错误（401/403），不应该使用后备验证
    // 401: 用户不存在或密码错误
    // 403: 用户未激活或已停用
    const statusCode = error?.response?.status;
    if (statusCode === 401 || statusCode === 403) {
      // 直接抛出错误，不使用后备验证
      throw error;
    }
    
    // 只有网络错误或其他系统错误时，才尝试使用本地验证作为后备（仅用于开发环境）
    // 注意：后备验证不检查用户状态，仅用于开发环境的快速测试
    const validatedUser = validateUserCredentials(credentials.username, credentials.password);
    if (validatedUser) {
      // 在后备验证中也检查用户状态
      if (validatedUser.status !== 'active') {
        // 如果用户状态不是 active，不允许登录
        throw new Error(`User account is ${validatedUser.status}, login not allowed`);
      }
      
      const token = `mock_token_${Date.now()}`;
      localStorage.setItem('user', JSON.stringify(validatedUser));
      localStorage.setItem('auth_token', token);
      return {
        success: true,
        token,
        user: validatedUser,
        expires_in: 3600 * 24,
      };
    }
    throw error;
  }
}

/**
 * 用户登出
 */
export async function logout(): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/api/auth/logout', {
    method: 'POST',
  });
}

/**
 * 获取当前用户信息
 */
export async function getCurrentUser(): Promise<{ success: boolean; user: User }> {
  return request<{ success: boolean; user: User }>('/api/auth/user', {
    method: 'GET',
  });
}

/**
 * 刷新token
 */
export async function refreshToken(): Promise<{ success: boolean; token: string; expires_in: number }> {
  return request<{ success: boolean; token: string; expires_in: number }>('/api/auth/refresh', {
    method: 'POST',
  });
}

/**
 * 当前用户修改密码（需验证原密码）
 */
export async function changePassword(data: {
  user_id: string;
  current_password: string;
  new_password: string;
}): Promise<{ success: boolean; message?: string }> {
  return request<{ success: boolean; message?: string }>('/api/auth/change-password', {
    method: 'POST',
    data,
  });
}

/**
 * 检查用户是否有指定权限
 */
export function hasPermission(user: User | null, permission: string): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  const permissions = user.permissions || [];
  return Array.isArray(permissions) && permissions.includes(permission);
}

/**
 * 检查用户是否有执行指定任务的权限
 */
export function canExecuteTask(user: User | null, taskPath: string): boolean {
  const requiredPermission = TASK_PERMISSIONS[taskPath];
  if (!requiredPermission) return true; // 如果没有定义权限要求，则允许访问
  return hasPermission(user, requiredPermission);
}

/**
 * 检查用户是否有多个权限中的任意一个
 */
export function hasAnyPermission(user: User | null, permissions: string[]): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  const userPermissions = user.permissions || [];
  return Array.isArray(userPermissions) && permissions.some((permission) => userPermissions.includes(permission));
}

/**
 * 检查用户是否有所有指定权限
 */
export function hasAllPermissions(user: User | null, permissions: string[]): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  const userPermissions = user.permissions || [];
  return Array.isArray(userPermissions) && permissions.every((permission) => userPermissions.includes(permission));
}

/**
 * 获取用户可访问的任务列表
 */
export function getAccessibleTasks(user: User | null, allTasks: any[]): any[] {
  if (!user) return [];
  if (isSuperAdmin(user)) return allTasks;

  return allTasks.filter(task => canExecuteTask(user, task.path));
}

// 所有角色和权限数据现在都从 DynamoDB 获取

// 获取本地存储的用户数据（仅作为缓存）
function getLocalUsers(): User[] {
  const stored = localStorage.getItem('mock_users');
  return stored ? JSON.parse(stored) : [];
}

// 保存用户数据到本地存储
function saveLocalUsers(users: User[]): void {
  localStorage.setItem('mock_users', JSON.stringify(users));
}

// 本地登录验证（仅作为后备，主要用于开发环境）
// 注意：生产环境应该完全依赖后端 API 验证
// 此函数已不再使用硬编码的 admin/admin123，所有用户验证都通过后端 API
export function validateUserCredentials(username: string, password: string): User | null {
  // 从本地缓存中查找用户（仅用于后备验证）
  // 注意：本地验证不包含密码验证，因为密码存储在 DynamoDB 中
  // 这个函数主要用于开发环境的快速测试，生产环境应该禁用
  const users = getLocalUsers();
  const user = users.find(u => u.username === username);

  if (user) {
    // 仅返回用户信息，不验证密码（密码验证应由后端完成）
    return user;
  }

  return null;
}

// 导出getStoredToken函数
export function getStoredToken(): string | null {
  return localStorage.getItem('auth_token');
}

// 导出getStoredUser函数
export function getStoredUser() {
  try {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Failed to parse stored user data:', error);
    return null;
  }
}

// 从后端 DynamoDB 获取所有用户
export async function getAllUsers(): Promise<{ success: boolean; data: User[] }> {
  const response = await request<{ success: boolean; data: User[] }>('/api/auth/users', {
    method: 'GET',
  });

  // 如果API成功，更新本地缓存
  if (response.success && response.data) {
    saveLocalUsers(response.data);
  }

  return response;
}

export async function getAllRoles(): Promise<{ success: boolean; data: Role[] }> {
  const response = await request<{ success: boolean; data: Role[] }>('/api/auth/roles', {
    method: 'GET',
  });
  return response;
}

export async function getAllPermissions(): Promise<{ success: boolean; data: Permission[] }> {
  const response = await request<{ success: boolean; data: Permission[] }>('/api/auth/permissions', {
    method: 'GET',
  });
  return response;
}

// 创建角色
export async function createRole(role: Omit<Role, 'id'>): Promise<{ success: boolean; role: Role }> {
  const response = await request<{ success: boolean; role: Role }>('/api/auth/roles', {
    method: 'POST',
    data: role,
  });
  return response;
}

// 更新角色
export async function updateRole(id: string, roleData: Partial<Role>): Promise<{ success: boolean; role: Role }> {
  const response = await request<{ success: boolean; role: Role }>(`/api/auth/roles/${id}`, {
    method: 'PUT',
    data: roleData,
  });
  return response;
}

// 删除角色
export async function deleteRole(id: string): Promise<{ success: boolean }> {
  const response = await request<{ success: boolean }>(`/api/auth/roles/${id}`, {
    method: 'DELETE',
  });
  return response;
}

// 创建权限
export async function createPermission(permission: Omit<Permission, 'id'>): Promise<{ success: boolean; permission: Permission }> {
  const response = await request<{ success: boolean; permission: Permission }>('/api/auth/permissions', {
    method: 'POST',
    data: permission,
  });
  return response;
}

// 更新权限
export async function updatePermission(id: string, permissionData: Partial<Permission>): Promise<{ success: boolean; permission: Permission }> {
  const response = await request<{ success: boolean; permission: Permission }>(`/api/auth/permissions/${id}`, {
    method: 'PUT',
    data: permissionData,
  });
  return response;
}

// 删除权限
export async function deletePermission(id: string): Promise<{ success: boolean }> {
  const response = await request<{ success: boolean }>(`/api/auth/permissions/${id}`, {
    method: 'DELETE',
  });
  return response;
}

// 创建用户
export async function createUser(user: Omit<User, 'id' | 'created_at' | 'last_login'>): Promise<{ success: boolean; user: User }> {
  const response = await request<{ success: boolean; user: User }>('/api/auth/users', {
    method: 'POST',
    data: user,
  });

  // 只有API成功时才更新本地存储（用于缓存，提高列表显示速度）
  if (response.success && response.user) {
    const users = getLocalUsers();
    const newUser: User = {
      ...user,
      id: response.user.id,
      created_at: response.user.created_at || new Date().toISOString(),
    };

    // 检查是否已存在（避免重复）
    const existingIndex = users.findIndex(u => u.id === newUser.id || u.username === newUser.username);
    if (existingIndex !== -1) {
      users[existingIndex] = newUser;
    } else {
      users.push(newUser);
    }
    saveLocalUsers(users);
  }

  return response;
}

// 更新用户
export async function updateUser(id: string, userData: Partial<User>): Promise<{ success: boolean; user: User }> {
  const response = await request<{ success: boolean; user: User }>(`/api/auth/users/${id}`, {
    method: 'PUT',
    data: userData,
  });

  // 只有API成功时才更新本地存储（用于缓存）
  if (response.success && response.user) {
    const users = getLocalUsers();
    const userIndex = users.findIndex(u => u.id === id);

    if (userIndex !== -1) {
      users[userIndex] = { ...users[userIndex], ...response.user };
      saveLocalUsers(users);
    }
  }

  return response;
}

// 删除用户
export async function deleteUser(id: string): Promise<{ success: boolean }> {
  const response = await request<{ success: boolean }>(`/api/auth/users/${id}`, {
    method: 'DELETE',
  });

  // 只有API成功时才更新本地存储（用于缓存）
  if (response.success) {
    const users = getLocalUsers();
    const filteredUsers = users.filter(u => u.id !== id);
    saveLocalUsers(filteredUsers);
  }

  return response;
}
